import { boundMethod } from 'autobind-decorator';
/**
 * This would be the _"root of all containers"_, the one that setups the app basic functionalities
 * and renders all the other containers.
 */
class AppContainer {
  /**
   * @param {AppAPI}           appAPI           To be able to set the authorization token when the
   *                                            session gets updated.
   * @param {AppConfiguration} appConfiguration To get the app title and add it to the router.
   * @param {AppRouting}       appRouting       To setup the routes and the authorization step on
   *                                            the pipeline.
   * @param {AppRoutingStep}   appRoutingStep   The authorization step that will validate the user
   *                                            permissions before accessing a route.
   * @param {AppSession}       appSession       To listen for session changes to update the UI.
   * @param {AppNavigation}    NAVIGATION       To get all the routes the app will use.
   */
  constructor(
    appAPI,
    appConfiguration,
    appRouting,
    appRoutingStep,
    appSession,
    NAVIGATION,
  ) {
    'inject';

    /**
     * A local reference for the `appAPI` service.
     * @type {AppAPI}
     * @access protected
     */
    this._appAPI = appAPI;
    /**
     * A local reference for the `appConfiguration` service.
     * @type {AppConfiguration}
     * @access protected
     */
    this._appConfiguration = appConfiguration;
    /**
     * A local reference for the `appRouting` service.
     * @type {AppRouting}
     * @access protected
     */
    this._appRouting = appRouting;
    /**
     * A local reference for the `appRoutingStep` service.
     * @type {AppRoutingStep}
     * @access protected
     */
    this._appRoutingStep = appRoutingStep;
    /**
     * A local reference for the `appSession` service.
     * @type {AppSession}
     * @access protected
     */
    this._appSession = appSession;
    /**
     * A local reference for the `NAVIGATION` constant.
     * @type {AppNavigation}
     * @access protected
     */
    this._NAVIGATION = NAVIGATION;
    /**
     * When the container gets activated, this property will hold the subscription for the event
     * triggered when the user session changes.
     * @type {?Subscription}
     */
    this._sessionSubscription = null;
    /**
     * Wether or not to show the menu on the UI.
     * @type {boolean}
     */
    this.showMenu = false;
  }
  /**
   * Starts listening for changes on the user session.
   */
  activate() {
    this._sessionSubscription = this._appSession.onSessionChange(this._onSessionUpdate);
    // We force the update to detect the menu and the token.
    this._onSessionUpdate();
  }
  /**
   * Add the authorization step on the router pipeline and define all the app routes.
   * @param {RouterConfiguration} config The router configuration.
   */
  configureRouter(config) {
    config.title = this._appConfiguration.get('appTitle');
    config.addPipelineStep('authorize', this._appRoutingStep);
    config.options.pushState = true;
    config.map(this._appRouting.extractRoutes(this._NAVIGATION));
  }
  /**
   * Stops listening for changes on the user session.
   */
  detached() {
    this._sessionSubscription.dispose();
  }
  /**
   * Updates the properties the UI uses when the user session changes.
   * @param {?Object} session If this is called from the SSO client after a successful
   *                          authentication, this object will contain the new session
   *                          information.
   * @access protected
   * @ignore
   */
  @boundMethod
  _onSessionUpdate(session) {
    if (session && session.token) {
      // If this was called from the SSO client, get the information from the parameter.
      this.showMenu = true;
      this._appAPI.setAuthorizationToken(session.token);
    } else if (this._appSession.isAuthenticated()) {
      // If this was called manually, get the information from the SSO client.
      this.showMenu = true;
      this._appAPI.setAuthorizationToken(this._appSession.getSession().token);
    } else if (this.showMenu) {
      // Only change it if the value was different.
      this.showMenu = false;
    }
  }
}

export { AppContainer };
